import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import WorkCard from "@/components/feature/WorkCard";
import { categories, works } from "@/mocks/works";

type SortKey = "hot" | "rating" | "update";

const sorts: { key: SortKey; label: string }[] = [
  { key: "hot", label: "人气最高" },
  { key: "rating", label: "评分最高" },
  { key: "update", label: "最近更新" },
];

export default function Works() {
  const [params, setParams] = useSearchParams();
  const [keyword, setKeyword] = useState(params.get("q") || "");
  const activeCat = params.get("cat") || "all";
  const sortKey = (params.get("sort") as SortKey) || "hot";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all" && value !== "hot") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const list = useMemo(() => {
    let result = [...works];
    if (activeCat !== "all") {
      result = result.filter((w) => w.category === categories.find((c) => c.id === activeCat)?.name);
    }
    const kw = keyword.trim().toLowerCase();
    if (kw) {
      result = result.filter(
        (w) =>
          w.title.toLowerCase().includes(kw) ||
          w.author.toLowerCase().includes(kw) ||
          w.tags.some((t) => t.toLowerCase().includes(kw))
      );
    }
    if (sortKey === "rating") result.sort((a, b) => b.rating - a.rating);
    else if (sortKey === "update") result.sort((a, b) => (a.isNew === b.isNew ? 0 : a.isNew ? -1 : 1));
    else result.sort((a, b) => parseInt(b.views, 10) - parseInt(a.views, 10));
    return result;
  }, [activeCat, keyword, sortKey]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParam("q", keyword);
  };

  return (
    <>
      <Navbar />
      <PageHeader
        crumbs={[
          { label: "首页", to: "/" },
          { label: "作品库" },
        ]}
        title="作品库"
        desc="按分类与偏好浏览全部漫画，支持按人气、评分和更新时间排序，快速找到对胃口的作品。"
      >
        <form onSubmit={handleSearch} role="search" className="mt-6 flex max-w-md items-center gap-0 rounded-full border border-background-300 bg-background-50 p-1.5">
          <i className="ri-search-line pl-3 text-lg text-foreground-500" />
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索作品 / 作者 / 标签"
            aria-label="搜索作品"
            className="flex-1 bg-transparent px-3 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600">
            搜索
          </button>
        </form>
      </PageHeader>

      <main className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2" role="group" aria-label="按分类筛选">
              <button
                type="button"
                onClick={() => setParam("cat", "all")}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCat === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                全部
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setParam("cat", c.id)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCat === c.id ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 rounded-full bg-background-100 p-1" role="group" aria-label="排序方式">
              {sorts.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setParam("sort", s.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    sortKey === s.key ? "bg-background-50 text-primary-700 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <p className="mt-5 text-sm text-foreground-500">
            共找到 <b className="text-primary-600">{list.length}</b> 部作品
          </p>

          {list.length > 0 ? (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {list.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          ) : (
            <div className="mt-16 flex flex-col items-center justify-center py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
                <i className="ri-inbox-line" />
              </span>
              <p className="mt-4 text-base text-foreground-600">没有找到相关作品，换个关键词试试吧</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setParam("q", "");
                  setParam("cat", "all");
                }}
                className="mt-4 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                清除筛选
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "叮咚漫画网 作品库",
            description: "按分类浏览叮咚漫画网全部作品，支持按人气、评分和更新时间排序。",
            url: "https://fangupdate.github.io/works",
            inLanguage: "zh-CN",
            isPartOf: { "@type": "WebSite", name: "叮咚漫画网", url: "https://fangupdate.github.io/" },
          }),
        }}
      />
    </>
  );
}